/**
 * Reine Aufbereitung der Abacus-Meldungen - ohne Angular, damit prüfbar.
 *
 * Der Jest-Aufbau dieses Projekts trägt derzeit nicht (er scheitert schon am
 * einzigen Bestandstest), deshalb liegt die Logik bewusst frei von Dekoratoren
 * und lässt sich unabhängig durchrechnen.
 */

export interface AbacusTransferMessage {
  messageText: string;
  /** yyyyMMddHHmmss in UTC - Zeitpunkt der Antwortdatei. */
  fileTimestamp: string;
  transferKind?: string;
  abacusNo?: string;
  datamolinoId?: string;
}

/** Gleiche Meldungen zu einer Zeile zusammengefasst. */
export interface GroupedMessage {
  text: string;
  count: number;
  /** Zeitstempel des jüngsten Auftretens. */
  newest: string;
  /** Zeitstempel des ältesten Auftretens. */
  oldest: string;
}

/** Reintext ohne HTML - Altbestandstexte tragen `<br>` und Formatierung. */
export function toPlainText(value: string): string {
  return (value || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Fasst identische Meldungen zusammen, Reihenfolge bleibt erhalten.
 *
 * Nötig, weil ein Beleg bei wiederholten Übertragungsversuchen denselben Fehler
 * immer wieder zurückbekommt: Am 11.09.2026 trug Beleg 180900 104 Meldungen,
 * davon zwei verschiedene. Ungefiltert wäre die Aufklappliste eine Wand aus
 * identischen Zeilen statt einer Information.
 */
export function groupMessages(messages: AbacusTransferMessage[]): GroupedMessage[] {
  const groups: GroupedMessage[] = [];
  for (const message of messages || []) {
    const text = toPlainText(message.messageText);
    const existing = groups.find((group) => group.text === text);
    if (existing) {
      existing.count++;
      existing.oldest = message.fileTimestamp;
    } else {
      groups.push({
        text,
        count: 1,
        newest: message.fileTimestamp,
        oldest: message.fileTimestamp,
      });
    }
  }
  return groups;
}

/** `yyyyMMddHHmmss` (UTC) zu `dd.MM.yyyy HH:mm` in Ortszeit. */
export function formatUtcTimestamp(value: string): string {
  if (!value || value.length < 12) {
    return value || '';
  }
  const iso =
    value.substring(0, 4) +
    '-' +
    value.substring(4, 6) +
    '-' +
    value.substring(6, 8) +
    'T' +
    value.substring(8, 10) +
    ':' +
    value.substring(10, 12) +
    ':' +
    (value.substring(12, 14) || '00') +
    'Z';
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return value;
  }
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    pad(date.getDate()) +
    '.' +
    pad(date.getMonth() + 1) +
    '.' +
    date.getFullYear() +
    ' ' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes())
  );
}
